import { Stack, Typography, Grid } from '@mui/material';

import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useNavigate } from 'react-router-dom';

import moment from 'moment-timezone';

import { uploadControlQualityFiveWData } from 'common/api';
import ButtonComp from 'common/components/Button/ButtonComp';
import FileUploadWithLargeDropArea from 'common/components/FileUploadWithLargeDropArea/FileUploadWithLargeDropArea';
import ColumnTwoLayoutWithDivider from 'common/components/layouts/ColumnTwoLayoutWithDivider/ColumnTwoLayoutWithDivider';
import TextFieldWithLabel from 'common/components/TextFieldWithLabel/TextFieldWithLabel';
import { BUTTON_LABELS } from 'common/constants';
import {
	AI_SIMULATION_TYPES,
	CONTROL_QUALITY_GRAMMAR_CHECK_COMP,
	CONTROL_5W_TYPE,
	CONTROL_QUALITY_COPY_PASTE,
	CONTROL_COLUMN_MAPPING_DATA,
} from 'common/constants/AISimulationConstants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import { RootState } from 'common/store/AppStore';
import {
	setBulkUploadFailedStatus,
	setBulkUploadSuccessStatus,
	setControlQualityRetry,
} from 'common/store/reducer/commonReducer';
import { setRegulationText } from 'common/store/reducer/simulationReducer';
import {
	removeHTMLTags,
	toSentenceCase,
	urlWithTenant,
} from 'common/utils/utilFunctions';

import BulkUploadAlertMessage from '../Components/BulkUploadModal/BulkUploadAlertMessage';
import BulkUploadModal, {
	IFinalMappedFileInfo,
} from '../Components/BulkUploadModal/BulkUploadModal';
import StepHeader from '../Components/StepHeader/StepHeader';

import styles from './ControlQualityGrammerCheck.module.scss';

const { CONTROL_QUALITY_GRAMMAR_CHECK } = AI_SIMULATION_TYPES;

const { OK } = HTTP_STATUS_CODES;

const {
	FILE_UPLOAD_DROP_AREA_TITLE,
	FILE_UPLOAD_DROP_AREA_SUBTITLE,
	DRAG_AREA_TITLE,
	ACCEPTED_FILE_TYPE,
	MAX_FILE_SIZE,
	MAX_FILE_LIMIT,
} = CONTROL_QUALITY_GRAMMAR_CHECK_COMP;
const { TITLE, SUB_TITLE } = CONTROL_QUALITY_COPY_PASTE;
const { SUBMIT } = BUTTON_LABELS;
const ControlQualityGrammerCheck = ({
	initialFiles = [],
}: {
	initialFiles?: File[];
}) => {
	const dispatch = useDispatch();

	const userTimeZone = moment.tz.guess();

	const navigate = useNavigate();
	const { simulationArena, controlQualityGrammerCheck, controlQuality } =
		rrcpDashboardRoutes;

	const [uploadedFiles, setUploadedFiles] = useState<File[]>(initialFiles);
	const [retry, setRetry] = useState<any>();
	const [uploadSuccessAlert, setUploadSuccessAlert] = useState<boolean>(false);
	const [uploadRetryAlert, setUploadRetryAlert] = useState<boolean>(false);

	const openModal = uploadedFiles.length > 0;
	const regulationText = useSelector(
		(state: RootState) => state.simulationArena.regulationText,
	);

	const isRetryEnabled = useSelector(
		(state: RootState) => state?.common?.isControlQualityRetry,
	);

	const handleClose = () => {
		setUploadedFiles([]);
		resetUploadStatusData();
	};

	const resetUploadStatusData = () => {
		dispatch(setControlQualityRetry(false));
		dispatch(setBulkUploadSuccessStatus(false));
		dispatch(setBulkUploadFailedStatus(false));
	};

	const callFileUploadApi = async (data: IFinalMappedFileInfo[]) => {
		const formData = new FormData();
		const updatedFiles = data;

		updatedFiles?.forEach(async (item: any) => {
			const filesInfo = item?.fileInfo;
			formData.append('file', filesInfo);
			const mapInfo = Object.fromEntries(item?.mappingInfo);
			const response: any = await uploadControlQualityFiveWData(
				mapInfo,
				formData,
				uploadedFiles,
				CONTROL_5W_TYPE,
				userTimeZone,
			);
			if (response?.statusCode === OK) {
				setUploadedFiles([]);
				setUploadSuccessAlert(true);
				setRetry(false);
				dispatch(setControlQualityRetry(false));
				dispatch(setBulkUploadSuccessStatus(true));
				dispatch(setBulkUploadFailedStatus(false));
			} else {
				dispatch(setBulkUploadFailedStatus(true));
				dispatch(setBulkUploadSuccessStatus(false));
				if (retry) {
					setUploadedFiles([]);
					// show toast message
					// dispatch(setControlQualityRetry(true));
				}
				if (isRetryEnabled) {
					setRetry(true);
				}
			}
		});
	};

	const handleSubmit = (values: any) => {
		callFileUploadApi(values);
	};

	const handleRetry = (values: any) => {
		if (!retry) {
			callFileUploadApi(values);
			dispatch(setControlQualityRetry(true));
		} else {
			setUploadRetryAlert(true);
			setUploadSuccessAlert(false);
			setUploadedFiles([]);
		}
	};

	const showNotification = () => {
		let showNotification = false;
		if (uploadSuccessAlert || uploadRetryAlert) {
			showNotification = true;
		}
		return showNotification;
	};

	useEffect(() => {
		if (!isRetryEnabled) {
			setRetry(false);
		}
	}, [isRetryEnabled]);

	useEffect(() => {
		if (uploadedFiles?.length === 0) {
			resetUploadStatusData();
		}
	}, [uploadedFiles]);

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		dispatch(setRegulationText(removeHTMLTags(event.target.value)));
	};
	const handleSubmitInputClick = () => {
		navigate(
			urlWithTenant(
				`/${simulationArena}/${controlQualityGrammerCheck}/${controlQuality}`,
			),
		);
	};
	const onAlertClose = () => {
		setUploadSuccessAlert(false);
		setUploadRetryAlert(false);
	};
	return (
		<Stack>
			<StepHeader title={CONTROL_QUALITY_GRAMMAR_CHECK} />
			{showNotification() && (
				<BulkUploadAlertMessage
					uploadSuccessAlert
					uploadRetryAlert
					onClose={onAlertClose}
				/>
			)}

			<ColumnTwoLayoutWithDivider
				leftComponent={
					<Stack gap={1} className={styles['section-container']}>
						<Grid gap={2} container className={styles['area-container']}>
							<Grid item sm={12}>
								<Typography className={styles.title}>
									{toSentenceCase(TITLE)}
								</Typography>
								<Typography className={styles['sub-title']}>
									{toSentenceCase(SUB_TITLE)}
								</Typography>
							</Grid>
							<Grid item sm={12} position="relative">
								<TextFieldWithLabel
									inputProps={{
										'data-testid': `quality-input-additional`,
									}}
									id="quality-textarea"
									rows={10}
									value={regulationText}
									className={styles.textarea}
									onChange={handleInputChange}
									multiLine
									showLabel
									required
									label="Paste regulation information here"
									wordLimit={12000}
									positionClass={styles['word-limit-position']}
									error={false}
									helperText=""
								/>
							</Grid>
						</Grid>
						<Grid container display="flex" flexDirection="row-reverse">
							<ButtonComp
								btnType="primary"
								data-testid="submit-btn-control"
								onClick={handleSubmitInputClick}
								disabled={!regulationText?.length}
							>
								{SUBMIT}
							</ButtonComp>
						</Grid>
					</Stack>
				}
				rightComponent={
					<FileUploadWithLargeDropArea
						title={FILE_UPLOAD_DROP_AREA_TITLE}
						subTitle={FILE_UPLOAD_DROP_AREA_SUBTITLE}
						dragAreaTitle={DRAG_AREA_TITLE(MAX_FILE_SIZE)}
						uploadedFiles={uploadedFiles}
						setUploadedFiles={setUploadedFiles}
						accept={ACCEPTED_FILE_TYPE}
						maxfileSize={MAX_FILE_SIZE}
						maxFilesLimit={MAX_FILE_LIMIT}
					/>
				}
			/>
			{openModal && uploadedFiles && (
				<BulkUploadModal
					filesData={uploadedFiles}
					modalHeading={FILE_UPLOAD_DROP_AREA_TITLE}
					openModal={openModal}
					handleClose={handleClose}
					handleSubmit={handleSubmit}
					setUploadedFiles={setUploadedFiles}
					handleRetry={handleRetry}
					columnsData={CONTROL_COLUMN_MAPPING_DATA}
				/>
			)}
		</Stack>
	);
};

export default ControlQualityGrammerCheck;
